import * as React from 'react';
import propz from 'propz';
import { History } from 'history';
import { programmeBookingFormatCountdown } from 'Src/helpers/clubProgramme/clubProgrammeBooking';
import { ProgrammeRow, ChildProgrammeStats } from './ParentClubProgrammes.types';

interface Props {
  history: History;
  row: ProgrammeRow;
  nowMs: number;
  statsByProgrammeChild: Record<string, Record<string, ChildProgrammeStats>>;
}

export const ParentClubProgrammesCard: React.FC<Props> = ({ history, row, nowMs, statsByProgrammeChild }) => {
  const id = String(row.id);
  const opensAt = propz.get(row, ['booking', 'opensAt'], null);
  const closesAt = propz.get(row, ['booking', 'closesAt'], null);
  const access = String(propz.get(row, ['booking', 'preBookingAccess'], '') || '').toUpperCase();

  const pending = propz.get(row, ['counts', 'pending'], 0);
  const draft = propz.get(row, ['counts', 'draft'], 0);
  const accepted = propz.get(row, ['counts', 'accepted'], 0);

  const windowText = [
    opensAt ? `Opens: ${new Date(opensAt).toLocaleString()}` : undefined,
    closesAt ? `Closes: ${new Date(closesAt).toLocaleString()}` : undefined
  ]
    .filter(Boolean)
    .join(' • ');

  const opensAtMs = opensAt ? new Date(opensAt).getTime() : null;
  const closesAtMs = closesAt ? new Date(closesAt).getTime() : null;

  const isBeforeOpen = !!(opensAtMs && nowMs < opensAtMs);
  const isOpen = !!(opensAtMs && closesAtMs && nowMs >= opensAtMs && nowMs < closesAtMs);
  const isClosed = !!(closesAtMs && nowMs >= closesAtMs);

  const secToOpen = opensAtMs && nowMs < opensAtMs ? Math.ceil((opensAtMs - nowMs) / 1000) : null;
  const secToClose = closesAtMs && nowMs < closesAtMs ? Math.ceil((closesAtMs - nowMs) / 1000) : null;

  const countdownText =
    secToOpen !== null
      ? `Opens in ${programmeBookingFormatCountdown(secToOpen)}`
      : secToClose !== null
      ? `Closes in ${programmeBookingFormatCountdown(secToClose)}`
      : isClosed
      ? 'Booking closed'
      : '';

  const showSummaryChips = isOpen || access === 'DRAFT';
  const showChildStats = isOpen || access === 'DRAFT';
  const disableChildButtonsBeforeOpen = isBeforeOpen && access === 'HIDE';

  const preOpenHideMessage =
    isBeforeOpen && access === 'HIDE' && opensAt
      ? `You will be able to book once booking opens on ${new Date(opensAt).toLocaleString()}.`
      : '';

  const isHideBeforeOpen = isBeforeOpen && access === 'HIDE';

  const preOpenAccessChip =
    isBeforeOpen && access === 'VIEW'
      ? 'Available to preview'
      : isBeforeOpen && access === 'DRAFT'
      ? 'Available to pre-select'
      : '';

  return (
    <article className={`parentCard parentClubProgrammeCard ${isHideBeforeOpen ? 'parentClubProgrammeCard--locked' : ''}`} key={id}>
      <div className="parentClubProgrammeCard__top">
        <div>
          <h2>{row.name || 'Programme'}</h2>
          {row.description ? <p>{row.description}</p> : null}
        </div>
        {countdownText ? (
          <span className={`parentClubProgrammeCard__state ${secToClose !== null ? 'is-open' : ''}`}>
            {countdownText}
          </span>
        ) : null}
      </div>

      {windowText ? <div className="parentClubProgrammeCard__window">{windowText}</div> : null}
      {preOpenAccessChip ? <span className="parentClubProgrammeCard__access">{preOpenAccessChip}</span> : null}
      {preOpenHideMessage ? <div className="parentClubProgrammeCard__notice">{preOpenHideMessage}</div> : null}

      {showSummaryChips ? (
        <div className="parentClubProgrammeCard__chips">
          <span><strong>{pending}</strong> available</span>
          <span><strong>{draft}</strong> draft</span>
          <span className="is-success"><strong>{accepted}</strong> submitted</span>
        </div>
      ) : null}

      <div className="parentClubProgrammeCard__children">
        <span className="parentClubProgrammeCard__childrenLabel">Book for</span>
        {row.childButtons.map(button => {
          const stats = statsByProgrammeChild?.[id]?.[button.permissionId];
          return (
            <button
              key={button.permissionId}
              type="button"
              disabled={disableChildButtonsBeforeOpen}
              onClick={() => history.push({
                pathname: `/clubProgrammes/${id}/booking`,
                search: `?playerPermissionId=${encodeURIComponent(button.permissionId)}`
              })}
            >
              <strong>{button.label}</strong>
              {showChildStats && stats ? (
                <span>{stats.available} available · {stats.draft} draft · {stats.accepted} sent</span>
              ) : null}
              <i aria-hidden="true">›</i>
            </button>
          );
        })}
      </div>
    </article>
  );
};

